import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, UserCog, ChevronDown, Info } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { LocationPicker } from "@/components/LocationPicker";
import {
  travelTypeMap, serviceTypeOptionsFor, pricingOptionsFor, supportsWaiting,
  RIDE_TYPES, TRIP_TYPES, mobilityPriceLine, mobilityWaitingLine, mobilityIsBookable,
} from "@/lib/travelTypes";

const Field = ({ label, children }) => (
  <label className="block text-[11px] font-semibold text-slate-500">{label}<div className="mt-1">{children}</div></label>
);

export function MobilityFields({ f, set }) {
  const t = travelTypeMap[f.travel_type] || {};
  const mob = f.mobility || {};
  const setMob = (patch) => set({ mobility: { ...mob, ...patch } });
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [waitOpen, setWaitOpen] = useState(!!mob.waiting?.enabled);

  useEffect(() => {
    api.get("/seller/vehicles/approved").then((r) => setVehicles(r.data.vehicles || [])).catch(() => {});
    api.get("/seller/drivers/approved").then((r) => setDrivers(r.data.drivers || [])).catch(() => {});
  }, []);

  const stOpts = serviceTypeOptionsFor(f.travel_type);
  const selectedSt = mob.service_types || [];
  const toggleSt = (s) => setMob({ service_types: selectedSt.includes(s) ? selectedSt.filter((x) => x !== s) : [...selectedSt, s] });

  const priceOpts = pricingOptionsFor(f.travel_type);
  const pm = mob.pricing_model || priceOpts[0]?.[0] || "quote";
  const isGoods = t.bucket === "goods";
  const routeFromTo = ["outstation", "intercity"].includes(mob.trip_type) || f.travel_type === "outstation";

  const pickVehicle = (vid) => {
    const v = vehicles.find((x) => x.vehicle_id === vid);
    setMob({ vehicle_id: vid, vehicle_label: v ? `${v.vehicle_type}${v.make_model ? " · " + v.make_model : ""}` : "", capacity: v?.seating_capacity || 0 });
  };

  return (
    <div className="mt-3 space-y-3" data-testid="mobility-fields">
      {/* Service type multi-select */}
      {stOpts.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-slate-500">Service type <span className="font-normal text-slate-400">(pick all that apply)</span></p>
          <div className="mt-1 flex flex-wrap gap-1.5" data-testid="mobility-service-types">
            {stOpts.map((s) => (
              <button type="button" key={s} onClick={() => toggleSt(s)} data-testid={`mob-st-${s.replace(/\W+/g, "-").toLowerCase()}`}
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${selectedSt.includes(s) ? "bg-sky-600 text-white" : "border border-slate-200 text-slate-600 hover:bg-white"}`}>{s}</button>
            ))}
          </div>
        </div>
      )}

      {/* Route */}
      <div className="grid grid-cols-2 gap-2">
        {routeFromTo ? (<>
          <Field label="From"><LocationPicker mode="hotel" value={mob.from || ""} onChange={(v) => setMob({ from: v })} testid="mob-from" placeholder="Search or type city…" allowRequest /></Field>
          <Field label="To"><LocationPicker mode="hotel" value={mob.to || ""} onChange={(v) => setMob({ to: v })} testid="mob-to" placeholder="Search or type city…" allowRequest /></Field>
        </>) : (<>
          <Field label={isGoods ? "Pickup point" : "Pickup"}><Input value={mob.pickup || ""} onChange={(e) => setMob({ pickup: e.target.value })} data-testid="mob-pickup" className="h-8" placeholder="Area / landmark" /></Field>
          <Field label={isGoods ? "Drop point" : "Drop"}><Input value={mob.drop || ""} onChange={(e) => setMob({ drop: e.target.value })} data-testid="mob-drop" className="h-8" placeholder="Area / landmark" /></Field>
        </>)}
      </div>

      {/* Vehicle + Driver */}
      <div className="grid grid-cols-2 gap-2">
        <Field label="Vehicle (approved)">
          <select value={mob.vehicle_id || ""} onChange={(e) => pickVehicle(e.target.value)} data-testid="mob-vehicle" className="h-8 w-full rounded-md border border-slate-200 px-2 text-sm">
            <option value="">Select vehicle…</option>
            {vehicles.map((v) => <option key={v.vehicle_id} value={v.vehicle_id}>{v.vehicle_type}{v.make_model ? ` · ${v.make_model}` : ""}{v.seating_capacity ? ` · ${v.seating_capacity} seats` : ""}</option>)}
          </select>
        </Field>
        <Field label="Driver (approved, optional)">
          <select value={mob.driver_id || ""} onChange={(e) => setMob({ driver_id: e.target.value })} data-testid="mob-driver" className="h-8 w-full rounded-md border border-slate-200 px-2 text-sm">
            <option value="">Select driver…</option>
            {drivers.map((d) => <option key={d.driver_id} value={d.driver_id}>{d.name}</option>)}
          </select>
        </Field>
      </div>
      {(vehicles.length === 0 || drivers.length === 0) && (
        <p className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-700" data-testid="mobility-fleet-hint">
          <Info className="h-3.5 w-3.5" /> Add & get vehicles/drivers approved in <Link to="/seller/fleet" className="font-semibold underline" data-testid="mobility-fleet-link">Vehicles & Drivers</Link>. A booking needs an approved vehicle + driver.
        </p>
      )}
      {mob.capacity > 0 && <p className="text-[11px] text-slate-400" data-testid="mobility-capacity">Passenger capacity from vehicle: <span className="font-semibold text-slate-600">{mob.capacity}</span> (a customer can't request more).</p>}

      {/* Ride & Trip type (passenger only) */}
      {!isGoods && (
        <div className="grid grid-cols-2 gap-2">
          <Field label="Ride type">
            <select value={mob.ride_type || "both"} onChange={(e) => setMob({ ride_type: e.target.value })} data-testid="mob-ride-type" className="h-8 w-full rounded-md border border-slate-200 px-2 text-sm">
              {RIDE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Trip type">
            <select value={mob.trip_type || "one_way"} onChange={(e) => setMob({ trip_type: e.target.value })} data-testid="mob-trip-type" className="h-8 w-full rounded-md border border-slate-200 px-2 text-sm">
              {TRIP_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        </div>
      )}

      {/* Pricing model + fields */}
      <div className="rounded-xl border border-slate-100 bg-white/70 p-2.5" data-testid="mobility-pricing">
        <p className="text-[11px] font-semibold text-slate-600">Availability &amp; Pricing</p>
        <div className="mt-1.5">
          <Field label="Pricing model">
            <select value={pm} onChange={(e) => setMob({ pricing_model: e.target.value })} data-testid="mob-pricing-model" className="h-8 w-full rounded-md border border-slate-200 px-2 text-sm">
              {priceOpts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {pm === "fixed" && <Money label="Base fare (₹)" v={mob.base_fare} onChange={(x) => setMob({ base_fare: x })} tid="mob-base-fare" />}
          {pm === "per_trip" && <Money label="Trip fare (₹)" v={mob.trip_fare} onChange={(x) => setMob({ trip_fare: x })} tid="mob-trip-fare" />}
          {(pm === "per_km" || pm === "per_km_hour") && <>
            <Money label="Rate per KM (₹)" v={mob.rate_km} onChange={(x) => setMob({ rate_km: x })} tid="mob-rate-km" />
            <Num label="Minimum KM" v={mob.min_km} onChange={(x) => setMob({ min_km: x })} tid="mob-min-km" />
          </>}
          {(pm === "per_hour" || pm === "per_km_hour") && <>
            <Money label="Rate per Hour (₹)" v={mob.rate_hour} onChange={(x) => setMob({ rate_hour: x })} tid="mob-rate-hour" />
            <Num label="Minimum hours" v={mob.min_hours} onChange={(x) => setMob({ min_hours: x })} tid="mob-min-hours" />
          </>}
          {pm === "per_passenger" && <>
            <Money label="Adult (₹)" v={mob.fare_a} onChange={(x) => setMob({ fare_a: x })} tid="mob-fare-a" />
            <Money label="Child (₹)" v={mob.fare_c} onChange={(x) => setMob({ fare_c: x })} tid="mob-fare-c" />
            <Money label="Infant (₹)" v={mob.fare_i} onChange={(x) => setMob({ fare_i: x })} tid="mob-fare-i" />
          </>}
          {pm === "quote" && <p className="col-span-2 text-[11px] text-slate-500">No price — customers submit their requirement and you reply with a quote.</p>}
        </div>
        <p className="mt-2 rounded-lg bg-sky-50 px-2.5 py-1.5 text-[11px] font-semibold text-sky-700" data-testid="mobility-price-preview">
          Customers will see: “{mobilityPriceLine({ ...mob, pricing_model: pm })}” · {mobilityIsBookable({ ...mob, pricing_model: pm }) ? "Book Now" : "Request Quote"}
        </p>
      </div>

      {/* Optional waiting charges */}
      {supportsWaiting(f.travel_type) && (
        <div className="rounded-xl border border-slate-100 p-2.5" data-testid="mobility-waiting">
          <button type="button" onClick={() => { const en = !waitOpen; setWaitOpen(en); setMob({ waiting: { ...(mob.waiting || {}), enabled: en } }); }} data-testid="mob-waiting-toggle" className="flex w-full items-center gap-2 text-left">
            <span className="text-[11px] font-semibold text-slate-600">Waiting charges <span className="font-normal text-slate-400">(optional)</span></span>
            <ChevronDown className={`ml-auto h-3.5 w-3.5 text-slate-400 transition-transform ${waitOpen ? "rotate-180" : ""}`} />
          </button>
          {waitOpen && (
            <div className="mt-2 grid grid-cols-2 gap-2" data-testid="mobility-waiting-fields">
              <Field label="Waiting rate (₹)"><Input type="number" value={mob.waiting?.rate ?? ""} onChange={(e) => setMob({ waiting: { ...(mob.waiting || {}), enabled: true, rate: e.target.value } })} data-testid="mob-waiting-rate" className="h-8" /></Field>
              <Field label="Charging unit">
                <select value={mob.waiting?.rate_unit || "min"} onChange={(e) => setMob({ waiting: { ...(mob.waiting || {}), rate_unit: e.target.value } })} data-testid="mob-waiting-unit" className="h-8 w-full rounded-md border border-slate-200 px-2 text-sm">
                  <option value="min">per minute</option><option value="15min">per 15 minutes</option><option value="hr">per hour</option>
                </select>
              </Field>
              <Field label="Grace period (mins free)"><Input type="number" value={mob.waiting?.grace_minutes ?? mob.waiting?.free_minutes ?? ""} onChange={(e) => setMob({ waiting: { ...(mob.waiting || {}), grace_minutes: e.target.value } })} data-testid="mob-waiting-grace" className="h-8" /></Field>
              <Field label="Maximum charge (₹, optional)"><Input type="number" value={mob.waiting?.max_charge ?? ""} onChange={(e) => setMob({ waiting: { ...(mob.waiting || {}), max_charge: e.target.value } })} data-testid="mob-waiting-max" className="h-8" /></Field>
              <Field label="Note (optional)"><Input value={mob.waiting?.note || ""} onChange={(e) => setMob({ waiting: { ...(mob.waiting || {}), note: e.target.value } })} data-testid="mob-waiting-note" className="h-8" /></Field>
              <p className="col-span-2 rounded-lg bg-sky-50 px-2.5 py-1.5 text-[11px] text-slate-500">Timer starts when the driver taps <b>Arrived</b> and stops at trip start. Chargeable = wait − grace, at the rate/unit above{mob.waiting?.max_charge ? `, capped at ₹${mob.waiting.max_charge}` : ""}. Auto-calculated — drivers never type it.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Money = ({ label, v, onChange, tid }) => (
  <Field label={label}><div className="relative"><span className="pointer-events-none absolute left-2 top-1.5 text-xs text-slate-400">₹</span><Input type="number" value={v ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} className="h-8 pl-5" /></div></Field>
);
const Num = ({ label, v, onChange, tid }) => (
  <Field label={label}><Input type="number" value={v ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} className="h-8" /></Field>
);
